import React, { useEffect, useState } from "react";
import { useReportJobPolling } from "@/export/hooks/use-report-job-polling";
import { ReportJob, JobStatus } from "@/export/types/reports.ts";
import toast from "react-hot-toast";
import { FileSpreadsheet, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ReportProgressToastProps {
  jobId: string;
  onComplete?: (job: ReportJob) => void;
}

export const ReportProgressToast: React.FC<ReportProgressToastProps> = ({
  jobId,
  onComplete,
}) => {
  const { t } = useTranslation("export");
  const [toastId, setToastId] = useState<string | null>(null);
  
  const {
    job,
    status,
    progress,
    isActive,
    isCompleted,
    isSuccessful,
    isFailed,
  } = useReportJobPolling({
    jobId,
    enabled: !!jobId,
    onComplete: (completedJob) => {
      onComplete?.(completedJob);
      // Dismiss the progress toast
      if (toastId) {
        toast.dismiss(toastId);
      }
    },
  });

  useEffect(() => {
    if (!job || !status) return;

    // Create or update the progress toast
    const progressText = isActive
      ? t("progressToast.exporting", {
          percent: progress?.percent || 0,
          stage: progress?.stage || t("progressToast.processing"),
        })
      : isSuccessful
        ? t("progressToast.completed")
        : isFailed
          ? t("progressToast.failed")
          : t("progressToast.status");

    const icon = isActive 
      ? <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
      : isSuccessful 
        ? <CheckCircle className="h-4 w-4 text-green-500" />
        : isFailed 
          ? <XCircle className="h-4 w-4 text-red-500" />
          : <FileSpreadsheet className="h-4 w-4" />;

    const toastOptions = {
      duration: isCompleted ? 5000 : Infinity, // Auto-dismiss only when completed
      position: 'bottom-right' as const,
      style: {
        background: isSuccessful ? '#f0f9ff' : isFailed ? '#fef2f2' : '#f8fafc',
        border: isSuccessful ? '1px solid #22c55e' : isFailed ? '1px solid #ef4444' : '1px solid #e2e8f0',
        color: '#1f2937',
        minWidth: '300px',
      },
      icon,
    };

    if (toastId) {
      // Update existing toast
      toast.loading(progressText, { id: toastId, ...toastOptions });
    } else {
      // Create new toast
      const id = toast.loading(progressText, toastOptions);
      setToastId(id);
    }
  }, [job, status, progress, isActive, isSuccessful, isFailed, isCompleted, toastId, t]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (toastId) {
        toast.dismiss(toastId);
      }
    };
  }, [toastId]);

  return null; // This component doesn't render anything visible
};

